<template>
  <section v-if="visibleModules.length" class="ni-section ni-recommended">
    <div class="ni-container ni-container--wide">
      <SectionHeader subtitle="TOP RECOMMENDED">
        <template #title>
          <span class="ni-section-header__title-mark">{{ t('index.hot') }}</span>
        </template>
      </SectionHeader>
      <div :ref="setListRef" class="ni-recommended__list">
        <template v-for="(mod, mIndex) in visibleModules" :key="`hot-mod-${mIndex}`">
          <!-- 满页拼贴 -->
          <template v-if="mod.layout !== 'grid'">
            <div
              v-if="mod.first.length"
              class="ni-recommended__row ni-recommended__row--first"
              :style="rowStyle"
            >
              <RouterLink
                v-for="(photo, index) in mod.first"
                :key="photo.id || `f-${mIndex}-${index}`"
                class="ni-recommended__photo"
                :style="firstPhotoStyle(index)"
                :to="buildPhotoDetailUrl(photo.id)"
              >
                <img :src="photo.preview || photo.thumb" :alt="photo.title || ''" loading="lazy" @contextmenu.prevent />
              </RouterLink>
            </div>
            <div
              v-if="mod.second.length"
              class="ni-recommended__row ni-recommended__row--second"
              :style="rowStyle"
            >
              <RouterLink
                v-for="(photo, index) in mod.second"
                :key="photo.id || `s-${mIndex}-${index}`"
                class="ni-recommended__photo"
                :class="{ 'ni-recommended__photo--tall': photo.first }"
                :style="secondPhotoStyle(index)"
                :to="buildPhotoDetailUrl(photo.id)"
              >
                <img :src="photo.preview || photo.thumb" :alt="photo.title || ''" loading="lazy" @contextmenu.prevent />
              </RouterLink>
            </div>
          </template>
          <!-- 不足一页：横竖自适应网格 -->
          <div v-else class="ni-recommended__grid">
            <RouterLink
              v-for="(photo, index) in mod.items"
              :key="photo.id || `g-${mIndex}-${index}`"
              class="ni-recommended__grid-item"
              :class="{ 'ni-recommended__grid-item--portrait': photo.first }"
              :to="buildPhotoDetailUrl(photo.id)"
            >
              <img :src="photo.preview || photo.thumb" :alt="photo.title || ''" loading="lazy" @contextmenu.prevent />
            </RouterLink>
          </div>
        </template>
      </div>
      <div v-if="allModules.length > 1 && !nomore" class="ni-recommended__loadmore">
        <button
          type="button"
          class="ni-recommended__more"
          @click="loadMore"
        >
          {{ t('loadmore') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { buildPhotoDetailUrl } from '@/utils/seoUrl.js';
import { buildHotMosaicModules, HOT_MOSAIC_PAGE_SIZE } from '@/utils/newIndexMappers.js';
import SectionHeader from './shared/SectionHeader.vue';

const props = defineProps({
  photos: { type: Array, default: () => [] },
  /** 每页张数：上行 2 + 下行 5，对齐原版铺满效果 */
  moduleSize: { type: Number, default: HOT_MOSAIC_PAGE_SIZE },
});

const LEFT_PART = 26.5;
const RIGHT_PART = 9.4;
const TOTAL_PART = LEFT_PART + RIGHT_PART;

const { t } = useI18n();
const listRef = ref(null);
const layoutWidth = ref(0);
const isMobile = ref(false);
const visibleCount = ref(1);
let resizeObserver;

const allModules = computed(() => buildHotMosaicModules(props.photos, props.moduleSize));
const visibleModules = computed(() => allModules.value.slice(0, visibleCount.value));
const nomore = computed(() => visibleCount.value >= allModules.value.length);

watch(
  () => props.photos,
  () => {
    visibleCount.value = 1;
  },
);

function loadMore() {
  if (nomore.value) return;
  visibleCount.value += 1;
}

function setListRef(el) {
  listRef.value = el;
  if (resizeObserver) resizeObserver.disconnect();
  if (!el) return;
  updateLayout();
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(el);
  }
}

function updateLayout() {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches;
  layoutWidth.value = listRef.value ? listRef.value.clientWidth : 0;
}

onMounted(() => {
  updateLayout();
  if (typeof ResizeObserver === 'undefined') {
    window.addEventListener('resize', updateLayout);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  window.removeEventListener('resize', updateLayout);
});

const rowStyle = computed(() => {
  if (!layoutWidth.value) return {};
  if (isMobile.value) return { width: '100%', height: 'auto' };
  const height = (layoutWidth.value * 16) / TOTAL_PART;
  return { height: `${height}px` };
});

function firstPhotoStyle(index) {
  if (!layoutWidth.value) return {};
  if (isMobile.value) return { width: '100%', height: 'auto' };
  const per = layoutWidth.value / TOTAL_PART;
  if (index === 0) {
    const width = LEFT_PART * per;
    return { width: `${width}px`, height: `${(width * 16) / LEFT_PART}px` };
  }
  if (index === 1) {
    const width = RIGHT_PART * per;
    return { width: `${width}px`, height: `${(width * 16) / RIGHT_PART}px` };
  }
  return {};
}

function secondPhotoStyle(index) {
  if (!layoutWidth.value) return {};
  if (isMobile.value) return { width: '100%', height: 'auto' };
  const firstWidth = RIGHT_PART * (layoutWidth.value / TOTAL_PART);
  if (index === 0) return { width: `${firstWidth}px` };
  return { width: `${(layoutWidth.value - firstWidth) / 2}px` };
}
</script>

<style scoped>
.ni-recommended :deep(.ni-section-header__bar) {
  display: none;
}

.ni-recommended__list {
  width: 100%;
  padding: 20px 0 0;
}

.ni-recommended__row {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 35.9 / 16;
}

.ni-recommended__photo {
  position: relative;
  display: block;
  overflow: hidden;
  padding: 8px;
  border-radius: 8px;
  box-sizing: border-box;
}

.ni-recommended__row--first .ni-recommended__photo {
  height: 100%;
}

.ni-recommended__row--second .ni-recommended__photo {
  width: 33.3%;
  height: 50%;
}

.ni-recommended__row--second .ni-recommended__photo--tall {
  height: 100%;
}

.ni-recommended__photo img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.ni-recommended__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  width: 100%;
  padding: 0 0 8px;
}

.ni-recommended__grid-item {
  display: block;
  overflow: hidden;
  padding: 8px;
  border-radius: 8px;
  box-sizing: border-box;
  aspect-ratio: 16 / 10;
}

.ni-recommended__grid-item--portrait {
  aspect-ratio: 3 / 4;
}

.ni-recommended__grid-item img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.ni-recommended__loadmore {
  width: 100%;
  padding: 16px 0 28px;
  text-align: center;
}

.ni-recommended__more {
  color: #409eff;
  padding: 7px 20px;
  border: 1px solid #409eff;
  box-shadow: 0 0 10px #f5f7fa;
  background-color: #f5f7fa;
  border-radius: 5px;
  cursor: pointer;
  font: inherit;
}

.ni-recommended__more:hover {
  background-color: #409eff;
  color: #fff;
}

.ni-recommended__end {
  color: #999;
  padding: 7px 20px;
  box-shadow: 0 0 10px #f5f7fa;
  background-color: #f5f7fa;
  border-radius: 5px;
}

@media (max-width: 768px) {
  .ni-recommended__row {
    aspect-ratio: auto;
    flex-wrap: nowrap;
    height: auto;
  }

  .ni-recommended__photo,
  .ni-recommended__row--first .ni-recommended__photo,
  .ni-recommended__row--second .ni-recommended__photo,
  .ni-recommended__row--second .ni-recommended__photo--tall {
    width: 100%;
    height: auto;
    padding: 5px;
  }

  .ni-recommended__photo img {
    width: 100%;
    height: 100%;
  }

  .ni-recommended__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ni-recommended__grid-item {
    padding: 5px;
  }
}
</style>
